<template>
  <PrimevueDialog
    :visible="visible"
    :modal="true"
    :dismissable-mask="true"
    :close-on-escape="true"
    :style="{ width: 'min(880px, calc(100vw - 1.5rem))' }"
    class="release-notes-dialog"
    @update:visible="handleVisibilityChange"
  >
    <template #header>
      <div class="release-notes-header">
        <span class="release-notes-header__icon" aria-hidden="true">
          <i class="fa fa-gift"></i>
        </span>
        <div>
          <div class="d-flex align-items-center flex-wrap gap-2">
            <h5 class="mb-0">Nouveautés Suite241</h5>
            <span class="badge release-notes-version">Version {{ releaseNote?.version }}</span>
          </div>
          <small v-if="releaseNote">Mise à jour du {{ formattedReleaseDate }}</small>
        </div>
      </div>
    </template>

    <div v-if="releaseNote" class="release-notes-content">
      <div class="release-notes-intro">
        <h6>{{ releaseNote.title }}</h6>
        <p>{{ releaseNote.summary }}</p>
      </div>

      <div class="row g-3">
        <div
          v-for="section in visibleSections"
          :key="section.title"
          class="col-12"
          :class="visibleSections.length >= 3 ? 'col-lg-4' : 'col-lg-6'"
        >
          <section class="release-notes-section h-100" :class="`release-notes-section--${section.tone}`">
            <h6>
              <i class="fa" :class="section.icon" aria-hidden="true"></i>
              {{ section.title }}
            </h6>
            <ul>
              <li v-for="item in section.items" :key="item">{{ item }}</li>
            </ul>
          </section>
        </div>
      </div>

      <aside v-if="releaseNote.important?.length" class="release-notes-important">
        <strong><i class="fa fa-circle-info" aria-hidden="true"></i> À retenir</strong>
        <ul class="mb-0">
          <li v-for="item in releaseNote.important" :key="item">{{ item }}</li>
        </ul>
      </aside>

      <div v-if="saveError" class="alert alert-danger py-2" role="alert">
        <i class="fa fa-triangle-exclamation me-1" aria-hidden="true"></i>
        {{ saveError }}
      </div>

      <label class="release-notes-confirmation" for="release-notes-understood">
        <input
          id="release-notes-understood"
          v-model="understood"
          class="form-check-input"
          type="checkbox"
          :disabled="isSaving"
        >
        <span>J’ai lu et compris les nouveautés de cette version</span>
      </label>
    </div>

    <template #footer>
      <button type="button" class="btn btn-outline-secondary btn-sm btn-icon-label" :disabled="isSaving" @click="dismiss">
        <i class="fa fa-clock" aria-hidden="true"></i>
        <span class="btn-label">Lire plus tard</span>
      </button>
      <AppLoadingButton
        type="button"
        class="btn btn-primary btn-sm btn-icon-label"
        :disabled="!understood"
        :loading="isSaving"
        loading-label="Enregistrement..."
        @click="acknowledge"
      >
        <i class="fa fa-check" aria-hidden="true"></i>
        <span class="btn-label">J’ai compris</span>
      </AppLoadingButton>
    </template>
  </PrimevueDialog>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { extractApiErrorMessage } from '@/support/apiErrors';
import { currentSpaVersion } from '@/support/appVersionRefresh';
import { isVersionNewer, releaseNoteFor, type ReleaseNoteSection } from '@/support/releaseNotes';

const ELIGIBLE_ROLES = ['gerant', 'support', 'superadmin', 'super_admin'];

interface ReleaseNotesDialogState {
  visible: boolean;
  understood: boolean;
  isSaving: boolean;
  saveError: string;
  dismissedForThisRun: boolean;
}

export default defineComponent({
  name: 'AppReleaseNotesDialog',
  data(): ReleaseNotesDialogState {
    return {
      visible: false,
      understood: false,
      isSaving: false,
      saveError: '',
      dismissedForThisRun: false,
    };
  },
  computed: {
    isBackofficeAudience(): boolean {
      return this.$route?.meta?.audience !== 'public' && /^\/backoffice(?:\/|$)/.test(this.$route?.path || '');
    },
    releaseNote() {
      return releaseNoteFor(currentSpaVersion());
    },
    currentUser() {
      return this.$store.getters['auth/currentUser'];
    },
    visibleSections(): ReleaseNoteSection[] {
      const role = String(this.currentUser?.role || '').trim().toLowerCase();
      return this.releaseNote?.sections.filter((section) => (
        !section.visibleToRoles?.length || section.visibleToRoles.includes(role)
      )) || [];
    },
    settingsLoadedForCurrentUser(): boolean {
      const rootState = this.$store.state as Record<string, unknown>;
      const userSettingsState = rootState.userSettings as { lastLoadedUserId?: number | null } | undefined;

      return Number(userSettingsState?.lastLoadedUserId || 0) === Number(this.currentUser?.id || 0);
    },
    acknowledgedVersion(): string {
      return this.$store.getters['userSettings/settings']?.notices?.release_notes_acknowledged_version || '';
    },
    shouldDisplay(): boolean {
      return Boolean(
        this.isBackofficeAudience
        && !this.dismissedForThisRun
        && this.releaseNote
        && this.currentUser
        && ELIGIBLE_ROLES.includes(String(this.currentUser.role).trim().toLowerCase())
        && this.settingsLoadedForCurrentUser
        && isVersionNewer(currentSpaVersion(), this.acknowledgedVersion)
      );
    },
    formattedReleaseDate(): string {
      if (!this.releaseNote?.releasedAt) return '';

      return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      }).format(new Date(`${this.releaseNote.releasedAt}T00:00:00Z`));
    },
  },
  watch: {
    shouldDisplay: {
      immediate: true,
      handler(value: boolean) {
        this.visible = value;
        if (!value) { this.understood = false; this.saveError = ''; }
      },
    },
    currentUser(current, previous) {
      if (current?.id !== previous?.id) {
        this.visible = false;
        this.understood = false;
        this.saveError = '';
        this.dismissedForThisRun = false;
      }
    },
  },
  methods: {
    handleVisibilityChange(value: boolean): void {
      if (!value) this.dismiss();
    },
    dismiss(): void {
      if (this.isSaving) return;

      this.visible = false;
      this.understood = false;
      this.saveError = '';
      this.dismissedForThisRun = true;
    },
    async acknowledge(): Promise<void> {
      if (!this.isBackofficeAudience || !this.understood || !this.releaseNote || this.isSaving) return;

      this.isSaving = true;
      this.saveError = '';

      try {
        const settings = this.$store.getters['userSettings/settings'];
        await this.$store.dispatch('userSettings/save', {
          settings: {
            ...settings,
            notices: {
              ...settings.notices,
              release_notes_acknowledged_version: this.releaseNote.version,
            },
          },
          silent: true,
        });
        this.visible = false;
        this.dismissedForThisRun = true;
      } catch (error) {
        this.saveError = extractApiErrorMessage(
          error,
          'Impossible d’enregistrer votre confirmation. Veuillez réessayer.'
        );
        this.visible = this.isBackofficeAudience;
      } finally {
        this.isSaving = false;
      }
    },
  },
});
</script>

<style scoped>
.release-notes-header {
  align-items: center;
  display: flex;
  gap: 0.8rem;
}

.release-notes-header__icon {
  align-items: center;
  background: color-mix(in srgb, var(--suite241-admin-primary, #198754) 14%, white);
  border-radius: 12px;
  color: var(--suite241-admin-primary, #198754);
  display: inline-flex;
  flex: 0 0 42px;
  font-size: 1.1rem;
  height: 42px;
  justify-content: center;
}

.release-notes-header small {
  color: #64748b;
  font-weight: 600;
}

.release-notes-version {
  background: var(--suite241-admin-primary, #198754);
  color: #fff;
}

.release-notes-content {
  color: #243043;
  max-height: min(68vh, 680px);
  overflow-y: auto;
  padding: 0.1rem 0.15rem 0.35rem;
}

.release-notes-intro {
  background: color-mix(in srgb, var(--suite241-admin-primary, #198754) 8%, white);
  border-left: 4px solid var(--suite241-admin-primary, #198754);
  border-radius: 0 10px 10px 0;
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;
}

.release-notes-intro h6,
.release-notes-intro p {
  margin-bottom: 0.25rem;
}

.release-notes-intro p {
  color: #5b687a;
  font-size: 0.9rem;
}

.release-notes-section {
  border: 1px solid #e4e9f0;
  border-radius: 10px;
  padding: 0.9rem;
}

.release-notes-section h6 {
  align-items: center;
  display: flex;
  font-size: 0.92rem;
  gap: 0.45rem;
}

.release-notes-section--primary h6 { color: var(--suite241-admin-primary, #0d6efd); }
.release-notes-section--success h6 { color: #198754; }
.release-notes-section--warning h6 { color: #a75d00; }

.release-notes-section ul,
.release-notes-important ul {
  margin: 0;
  padding-left: 1.1rem;
}

.release-notes-section li,
.release-notes-important li {
  font-size: 0.82rem;
  line-height: 1.4;
  margin-bottom: 0.4rem;
}

.release-notes-important {
  background: #fff8e5;
  border: 1px solid #f6d789;
  border-radius: 10px;
  margin: 1rem 0;
  padding: 0.75rem 0.9rem;
}

.release-notes-important strong {
  color: #875000;
  display: block;
  font-size: 0.86rem;
  margin-bottom: 0.4rem;
}

.release-notes-confirmation {
  align-items: flex-start;
  border: 1px solid #dce3ec;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  font-size: 0.9rem;
  font-weight: 700;
  gap: 0.65rem;
  padding: 0.8rem 0.9rem;
}

.release-notes-confirmation .form-check-input:checked {
  background-color: var(--suite241-admin-primary, #198754);
  border-color: var(--suite241-admin-primary, #198754);
}

@media (max-width: 575.98px) {
  .release-notes-content { max-height: 65vh; }
  .release-notes-header__icon { display: none; }
}
</style>
